'use client';

import { List, StoreListingCard, StoreSearchDrawer } from '@petsmart-ui/sparky';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    getSearchStores,
    getStoreDisplayTime,
    PageType,
    storeIsOpen,
    useAutoshipDecisions,
    useCart,
    usePageLayout,
    useStore,
    useStoreDispatch,
} from '@libs/common-next';
import type { UpdateItemFulfillmentMethodRequestBody } from '@libs/common-node';
import type { SearchStore } from '@libs/common-types';
import { DeliveryType, getLocale, getStoreDetailsPathname, isStoreBopisEnabled, NAVIGATION_URLS } from '@libs/core';

import { trackNoLocationsFound, trackSelectStore } from './analytics';
import type { StoreDrawerProps } from './types';
import { useDrawerSide } from './use-drawer-side';
import {
    convertStoreIdToDemandwareFormat,
    enhanceStoreListWithInventory,
    filterStoreListToShow,
    getAlertText,
    getUserLocation,
    resolvePlpPickedStoreNumberSync,
    resolvePreferredPickedStoreNumber,
    storeSearchHasStock,
    useSearchDrawerSku,
} from './utils';
import './StoreDrawer.scss';

export default function StoreDrawer({
    drawerHeading,
    showStock,
    toggleText,
    className,
    pageType: defaultPageType,
}: StoreDrawerProps) {
    const {
        setStoreDrawerOpen,
        setFullStoreListResults,
        fullStoreListResults,
        storeDrawerOpen,
        setCartStoreDrawerOpen,
        cartStoreDrawerOpen,
        currentVariantId,
        currentBopisMinimumCaseQty,
        setContactUsFormStore,
    } = usePageLayout();
    const { drawerSide, isDrawerActive } = useDrawerSide(storeDrawerOpen, cartStoreDrawerOpen);
    const pageType = storeDrawerOpen?.pageType || defaultPageType;
    const { updateItemFulfillmentMethod } = useCart();
    const {
        selectedStore: { data: store },
    } = useStore();
    const { updateSelectedStore } = useStoreDispatch();
    const sku = useSearchDrawerSku(pageType);
    const isHeader = pageType === 'header';
    const [isLoading, setIsLoading] = useState(false);
    const [ranIPAutoSearch, setRanIPAutoSearch] = useState(false);
    const [showInventoryOnly, setShowInventoryOnly] = useState<boolean>(false);
    const [searchBoxText, setSearchBoxText] = useState<string | null>(null);
    const [pickedStore, setPickedStore] = useState<number | null>(() =>
        pageType === PageType.PLP ? null : (store?.StoreNumber ?? null)
    );
    const prevGlobalStoreNumberRef = useRef<number | undefined>(store?.StoreNumber);
    const pickedStoreRef = useRef(pickedStore);
    pickedStoreRef.current = pickedStore;

    const { isAutoshipCrawl } = useAutoshipDecisions();

    const componentClassName = cn(
        'store-drawer',
        drawerSide === 'left' && 'store-drawer--left',
        isHeader && 'header-store-drawer',
        className
    );
    const isCPC = pageType === PageType.CART;
    const storeListResultsToShow = useMemo(
        () => filterStoreListToShow(fullStoreListResults, !isHeader && showInventoryOnly),
        [fullStoreListResults, showInventoryOnly, isHeader]
    );
    const showAlert = !isLoading && storeListResultsToShow.length === 0;
    const disableStoresWithoutBopis = pageType === PageType.PLP;
    const showStoreDetails = Boolean(storeDrawerOpen?.showStoreDetails);
    const includeAttributes = Boolean(sku) || disableStoresWithoutBopis;
    const isStoreSelectable = useCallback(
        (store: SearchStore) => !disableStoresWithoutBopis || isStoreBopisEnabled(store),
        [disableStoresWithoutBopis]
    );
    const pickedSearchStore = useMemo(
        () => (pickedStore == null ? undefined : fullStoreListResults.find(s => s.Store.StoreNumber === pickedStore)),
        [pickedStore, fullStoreListResults]
    );
    const canConfirmPickedStore = Boolean(pickedSearchStore && isStoreSelectable(pickedSearchStore));
    const showStoreConfirmButton = isHeader || pickedStore !== null;
    const isPickedStoreInList =
        pickedStore != null && storeListResultsToShow.some(s => s.Store.StoreNumber === pickedStore);

    const allStoresURL = NAVIGATION_URLS.legacy.stores;

    const onToggleHasItems = () => {
        setShowInventoryOnly(!showInventoryOnly);
    };

    const resetResults = () => {
        setIsLoading(true);
        setPickedStore(null);
    };

    const finishLoadingStores = useCallback(
        async (storeList: SearchStore[]) => {
            const filteredStoreList =
                includeAttributes && !disableStoresWithoutBopis
                    ? storeList?.filter(storeItem => storeItem.attributes?.bopisEnabled)
                    : storeList;
            const enhancedStoreList = await enhanceStoreListWithInventory(
                filteredStoreList,
                sku,
                currentBopisMinimumCaseQty
            );

            const atLeastOneInStock = Boolean(enhancedStoreList?.some(storeSearchHasStock));

            setIsLoading(false);
            setShowInventoryOnly(atLeastOneInStock);
            setFullStoreListResults(enhancedStoreList);

            return enhancedStoreList;
        },
        [currentBopisMinimumCaseQty, disableStoresWithoutBopis, includeAttributes, setFullStoreListResults, sku]
    );

    const doStoreSearch = useCallback(
        async ({ lat, lng, query }: { lat?: number; lng?: number; query?: string }) => {
            resetResults();

            const storeList = await getSearchStores({
                lat,
                lng,
                query,
                radius: 300000,
                includeAttributes,
            });

            const enhancedStoreList = await finishLoadingStores(storeList);

            if (!enhancedStoreList?.length) {
                trackNoLocationsFound();
            }
        },
        [finishLoadingStores, includeAttributes]
    );

    const handleOnSearch = useCallback(async () => {
        if (searchBoxText) {
            await doStoreSearch({ query: searchBoxText });
        }
    }, [searchBoxText, doStoreSearch]);

    const handleUseMyLocation = async () => {
        const { isGeolocationEnabled, latitude: lat, longitude: lng } = await getUserLocation();

        if (!isGeolocationEnabled && ranIPAutoSearch) {
            // TODO: handle case where user denies location data but calls myLocationSearch
            return;
        }

        setSearchBoxText(null);
        await doStoreSearch({ lat, lng });
    };

    const closeDrawer = useCallback(() => {
        setStoreDrawerOpen(null);
        setCartStoreDrawerOpen(prevState => ({
            ...prevState,
            open: false,
        }));
    }, []);

    const handleSelectStoreCPC = async (store: number) => {
        const pickedStoreDemandwareFormat = convertStoreIdToDemandwareFormat(store);
        const requestBodyForChangeStoreCPC: UpdateItemFulfillmentMethodRequestBody = {
            fulfillmentMethod: DeliveryType.BOPIS,
            storeId: pickedStoreDemandwareFormat,
            itemId: cartStoreDrawerOpen.item?.id ?? '',
            ...(isAutoshipCrawl &&
                cartStoreDrawerOpen.item?.autoshipFrequency && {
                    autoshipFrequency: String(cartStoreDrawerOpen.item.autoshipFrequency),
                    autoshipFrequencyUnitOfMeasure: String(cartStoreDrawerOpen.item.autoshipFrequencyUnitOfMeasure),
                }),
        };
        await updateItemFulfillmentMethod(requestBodyForChangeStoreCPC);
        closeDrawer();
    };

    const handleSelectStoreContactUs = async (store: number) => {
        const updatedStore = fullStoreListResults.find(s => s.Store.StoreNumber === store)?.Store;
        setContactUsFormStore(updatedStore);
        closeDrawer();
    };

    const onDrawerSelectStore = async (pickedStoreParam?: number) => {
        // if param is sent, use that one
        // otherwise use pickedStore state
        const selectedStore = pickedStoreParam ?? pickedStore;
        if (!selectedStore) {
            return;
        }

        const selectedSearchStore = fullStoreListResults.find(s => s.Store.StoreNumber === selectedStore);
        if (!selectedSearchStore || !isStoreSelectable(selectedSearchStore)) {
            return;
        }

        trackSelectStore(String(selectedStore));

        if (isCPC && cartStoreDrawerOpen.item) {
            return handleSelectStoreCPC(selectedStore);
        }

        if (storeDrawerOpen?.pageType === 'contactUs') {
            return handleSelectStoreContactUs(selectedStore);
        }

        updateSelectedStore(selectedSearchStore.Store.StoreNumber);
        closeDrawer();
    };

    const getInitialStoreList = useCallback(async () => {
        let storeList: SearchStore[] = [];
        if (!ranIPAutoSearch) {
            storeList = await getSearchStores({
                includeAttributes: true,
            });
            setRanIPAutoSearch(true);
        }

        return storeList;
    }, [ranIPAutoSearch]);

    // PDP/CART: sync only when the global selected store changes — not when the list refreshes (sku/inventory).
    useEffect(() => {
        if (disableStoresWithoutBopis || !store?.StoreNumber) {
            return;
        }

        setPickedStore(store.StoreNumber);
    }, [store?.StoreNumber, disableStoresWithoutBopis]);

    // PLP: sync pick when global store or search list changes — read pickedStore from ref so user card clicks do not re-trigger.
    useEffect(() => {
        if (!disableStoresWithoutBopis) {
            return;
        }

        if (!store?.StoreNumber) {
            prevGlobalStoreNumberRef.current = undefined;
            setPickedStore(null);
            return;
        }

        const globalStoreChanged = prevGlobalStoreNumberRef.current !== store.StoreNumber;
        prevGlobalStoreNumberRef.current = store.StoreNumber;

        const nextPick = resolvePlpPickedStoreNumberSync({
            storeList: fullStoreListResults,
            globalStoreNumber: store.StoreNumber,
            pickedStore: pickedStoreRef.current,
            globalStoreChanged,
            isStoreSelectable,
        });

        if (nextPick !== undefined) {
            setPickedStore(nextPick);
        }
    }, [disableStoresWithoutBopis, store?.StoreNumber, fullStoreListResults, isStoreSelectable]);

    // Update the store list with fresh inventory data when sku is updated
    useEffect(() => {
        finishLoadingStores(fullStoreListResults);
    }, [sku]);

    useEffect(() => {
        // if drawer is opened and has at least one store in list that has stock, only show those stores
        if (Boolean(storeDrawerOpen) || cartStoreDrawerOpen.open) {
            const atLeastOneInStock = storeListResultsToShow.some(storeSearchHasStock);

            setShowInventoryOnly(atLeastOneInStock);

            if (!atLeastOneInStock) {
                handleUseMyLocation();
            }
        }
    }, [storeDrawerOpen, cartStoreDrawerOpen.open, sku]);

    useEffect(() => {
        // PDP only code. !fullStoreListResults.length and showStock are falsy on PLP
        if (!fullStoreListResults.length && showStock && currentVariantId && storeDrawerOpen) {
            setIsLoading(true);
            getInitialStoreList().then(async storeList => {
                if (!storeList?.length) {
                    return;
                }
                finishLoadingStores(storeList);
            });
        }
    }, [
        currentBopisMinimumCaseQty,
        currentVariantId,
        finishLoadingStores,
        fullStoreListResults.length,
        getInitialStoreList,
        setFullStoreListResults,
        showStock,
        sku,
        storeDrawerOpen,
    ]);

    useEffect(() => {
        // PLP only code
        if (!fullStoreListResults.length && !showStock) {
            getInitialStoreList().then(storeList => {
                if (!storeList?.length) {
                    return;
                }

                setFullStoreListResults(storeList);

                if (store?.StoreNumber) {
                    const preferred = resolvePreferredPickedStoreNumber(storeList, store.StoreNumber);
                    if (preferred != null) {
                        setPickedStore(preferred);
                    }
                } else {
                    const firstBopisStore = storeList.find(s => isStoreBopisEnabled(s));
                    if (firstBopisStore) {
                        setPickedStore(firstBopisStore.Store.StoreNumber);
                    }
                }
            });
        }
    }, [
        fullStoreListResults,
        showStock,
        currentVariantId,
        store,
        setPickedStore,
        setFullStoreListResults,
        getInitialStoreList,
    ]);

    const renderStoreCard = (s: SearchStore) => {
        const { HasCurrentProductInStock: hasInventory } = s.Store;
        const disabled = !isStoreSelectable(s);

        const timezonedDate = new Date().toLocaleString('en', {
            timeZone: s.Store.TimeZone?.TimeZoneId,
        });

        // date we are comparing to uses the timezone of the store we are using
        const currentDate = new Date(timezonedDate);

        return (
            <List.Item
                key={s.Store.StoreNumber}
                className={disabled ? 'store-drawer__store-row--no-bopis' : undefined}
                onClick={
                    disabled
                        ? undefined
                        : event => {
                              if (event.target instanceof Element && event.target.closest('a')) {
                                  return;
                              }

                              setPickedStore(s.Store.StoreNumber);
                          }
                }
            >
                <div
                    ref={(node: HTMLDivElement | null) => {
                        const radio = node?.querySelector<HTMLInputElement>('.sparky-c-radio__input');
                        if (radio) {
                            radio.disabled = disabled;
                        }
                    }}
                >
                    <StoreListingCard
                        data-testid={`store-test-${s.Store.StoreNumber}`}
                        address={{
                            addressName: s.Store.Name,
                            street: s.Store.StreetLine1,
                            city: s.Store.City,
                            state: s.Store.StateProvinceAbbreviation,
                            postalCode: s.Store.ZipPostalCode,
                        }}
                        stockStatus={s.Store.CurrentProductStockstatus}
                        showStock={isHeader ? false : showStock}
                        selected={pickedStore === s.Store.StoreNumber}
                        hasRadio
                        hasItems={hasInventory}
                        rating={undefined}
                        openText={getStoreDisplayTime(s.Store, currentDate, isHeader ? 'header' : 'default')}
                        distance={
                            s.DistanceToStoreFromOrigin > 0
                                ? `${s.DistanceToStoreFromOrigin.toFixed(1)} ${s.UnitOfDistance.replace(
                                      'kilometers',
                                      'kilometres'
                                  )}`
                                : undefined
                        }
                        closed={!storeIsOpen(s.Store, currentDate)}
                        myStore={store?.StoreNumber === s.Store.StoreNumber}
                        {...(showStoreDetails && {
                            storeDetailsUrl: getStoreDetailsPathname(s.Store).toLowerCase(),
                        })}
                    />
                </div>
            </List.Item>
        );
    };

    const alertContent = getAlertText(searchBoxText ?? '');
    const hasItems = !isHeader && showInventoryOnly;

    return (
        <StoreSearchDrawer
            viewStoresLink={allStoresURL}
            align={drawerSide === 'left' ? undefined : 'right'}
            data-testid="store-drawer"
            className={componentClassName}
            isActive={isDrawerActive}
            onClose={() => {
                setStoreDrawerOpen(null);
                setCartStoreDrawerOpen(prevState => ({
                    ...prevState,
                    open: false,
                }));
            }}
            showAlert={showAlert && !isLoading}
            showList={!showAlert && !isLoading}
            showStoreConfirmButton={showStoreConfirmButton}
            onUseMyLocation={handleUseMyLocation}
            onSearch={handleOnSearch}
            toggleText={isHeader ? undefined : toggleText}
            onSelectStore={() => onDrawerSelectStore()}
            isLoading={isLoading}
            hasItems={hasItems}
            drawerHeading={isHeader ? undefined : drawerHeading}
            alertIcon="exclamation-circle-fill"
            alertHeading={alertContent?.title ?? 'No locations found'}
            placeholder={getLocale() === 'en-CA' ? 'City or postal code' : 'City, state or zip code'}
            alertText={
                alertContent?.text ? (
                    <>
                        {alertContent?.text}
                        <a href={alertContent?.link} target="_blank" rel="noreferrer">
                            {alertContent?.linkText}
                        </a>
                    </>
                ) : (
                    ''
                )
            }
            onToggleHasItems={onToggleHasItems}
            onSearchBoxChange={(val: string) => setSearchBoxText(val)}
            value={searchBoxText ?? ''}
            disableStoreConfirmButton={
                isHeader
                    ? isLoading || !isPickedStoreInList
                    : storeListResultsToShow.length === 0 ||
                      isLoading ||
                      (disableStoresWithoutBopis && !canConfirmPickedStore)
            }
        >
            <List data-testid="store-drawer-list">{storeListResultsToShow.map(renderStoreCard)}</List>
        </StoreSearchDrawer>
    );
}
